<template>
  <div class="input__container">
    <v-text-field
      v-model="search"
      :label="$t(`${namespace}.startTypingToSearch`)"
      prepend-inner-icon="search"
      :loading="isLoading"
      color="accent"
      clearable
      flat
      outline
      solo
      @keyup="searchInput"
    />
    <!-- @click:clear="resetCompany" -->

    <ul
      class="input__list"
      :class="{ 'input__list--border-top': items.length }"
    >
      <li
        v-for="(item, index) in items"
        :key="index"
        class="input__list__item"
      >
        <div @click="setCompany(item)">
          <div class="text--ellipsis">{{ item.name }}</div>
          <span class="text--ellipsis">{{ item.displayName }} - {{ item.documentId | cnpj }}</span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { hasRole, isObject } from 'utils/functions'
import debounce from 'lodash.debounce'

export default {
  name: 'AutoCompleteCompany',
  computed: {
    ...mapState({
      company: ({ company }) => company.selectedCompany
    })
  },
  data () {
    return {
      namespace: 'Components.AutoCompleteCompany',
      search: null,
      isLoading: false,
      items: []
    }
  },
  methods: {
    ...mapActions({
      updateSelectedCompany: 'company/updateSelectedCompany',
      clearSelectedCompany: 'company/clearSelectedCompany',
      errorAlert: 'snackbarAlert/error',
      infoAlert: 'snackbarAlert/info'
    }),

    setCompany (company) {
      this.search = company.name
      this.items = []
      this.updateCompany(company)
    },

    resetCompany () {
      this.$emit('resetCompany')
    },

    async searchAPI () {
      if (!this.search) return

      this.isLoading = true

      try {
        let search = this.search
        if (search.length === 18 && /\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/.test(search)) {
          search = search.replace(/[-./]/g, '')
        }
        const { data: res } = await this.$apiService.searchCompany(search)
        this.items = res.data
        if (!res.data.length) this.infoAlert(this.$t(`${this.namespace}.companyNotFound`))
      } catch (err) {
        this.errorAlert(this.$t('Messages.genericError'))
        console.log('Error', err)
      }

      this.isLoading = false
    },

    async getWalletFinancialConfig (company) {
      try {
        const { data: walletFinancialConfig } = await this.$apiService.getWalletFinancialConfig(company.id)
        const { financialAccountId = null, periodicityConfigType = null, paymentMethod = null } = walletFinancialConfig.content

        this.updateSelectedCompany({
          ...company,
          financialAccountId,
          periodicityConfigType,
          paymentMethod
        })
      } catch (err) {
        console.log('ERROR', err)
      }
    },

    async updateCompany (company) {
      if (!company) {
        this.clearSelectedCompany(company)
        return
      }

      try {
        let plan = null

        if (this.hasRole('company', 'ROLE_GET_COMPANY_ID_CONFIG')) {
          const { data: companyConfig } = await this.$apiService.getCompanyConfig(company.id)
          plan = this.isValidObject(companyConfig.data) && companyConfig.data.plan
            ? companyConfig.data.plan
            : null
        }

        const updateCompany = {
          ...company,
          plan
        }

        this.updateSelectedCompany(updateCompany)
        await this.getWalletFinancialConfig(updateCompany)

        this.$emit('change')
      } catch (err) {
        this.errorAlert(this.$t('Messages.genericError'))
        console.log('Error', err)
      }
    },

    searchInput: debounce(function () {
      this.searchAPI()
    }, 1000),

    hasRole (group, role) {
      return hasRole(group, role)
    },

    isValidObject (val) {
      return isObject(val)
    }
  },
  created () {
    if (this.company) {
      this.search = this.company.name
    }
  },
  watch: {
    search (val) {
      if (!val) this.items = []
    },

    company (val) {
      if (val) {
        this.search = val.name
        return
      }

      this.search = null
    }
  }
}
</script>

<style lang="scss">
.input__container {

  .theme--light.v-text-field--outline > .v-input__control > .v-input__slot {
    transition: all .26s;
  }

  .theme--light.v-text-field--outline:not(.v-input--is-focused):not(.v-input--is-label-active) > .v-input__control > .v-input__slot,
  .theme--light.v-text-field--outline:not(.v-input--is-focused):not(.v-input--is-label-active) > .v-input__control > .v-input__slot:hover {
    border-color: #f0f0f0 !important;
    background-color: #f0f0f0 !important;
  }

  .theme--light.v-text-field--outline.v-input--is-label-active {

    > .v-input__control > .v-input__slot {
      background-color: #fff !important;
      border-color: #D6D6D6 !important;
    }

    .v-icon {
      color: $accent !important;
    }
  }

  .theme--light.v-text-field--outline.v-input--is-focused {

    .v-input__control > .v-input__slot,
    .v-input__control > .v-input__slot:hover {
      background-color: #fff !important;
      border-color: $accent-2 !important;
    }

    .v-icon {
      color: $accent !important;
    }
  }

  .theme--light.v-text-field--outline > .v-input__control > .v-input__slot {

    label, input {
      font-size: 12px;
    }
  }
}
</style>

<style lang="scss" scoped>
.input__container {
  width: 100%;
}

.input__list {
  max-height: 256px;
  list-style-type: none;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0;
  max-width: 372px;;

  &.input__list--border-top {
    border-top: 1px solid $gray-4;
  }
}

.input__list__item {
  border-top: 1px solid $gray-4;
  cursor: pointer;
  padding: 12px 16px;
  transition: background .26s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  &:first-child {
    border-top: none;
  }

  div {
    font-size: 14px;
  }

  span {
    color: $gray-6;
    display: block;
    font-size: 12px;
  }
}
</style>
